import { Box, Link, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';

import { LinkPreview } from '@atoms';
import { defaultStyleIcon } from './styles';
import { TableLinksManagement, LinkReviewCategoryMapper } from './types';
import { HistoryIcon } from '@atoms/Icons/HistoryIcon';
import { DATE_FORMAT } from '@constants';
import LinkReviewStatus from './components/LinkReviewStatus';

interface IGetColumns {
  setSelectedLink: (selectedLink: TableLinksManagement) => void;
  setEditingNote: (note: string) => void;
  setOpenNoteModal: (isOpen: boolean) => void;
  setOpenHistoryModal: (isOpen: boolean) => void;
}

export const getColumns = (params: IGetColumns): GridColDef<TableLinksManagement>[] => {
  const { setSelectedLink, setEditingNote, setOpenNoteModal, setOpenHistoryModal } = params;

  return [
    {
      field: 'actions',
      headerName: '',
      width: 40,
      filterable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" gap="4px">
          <Tooltip title="Show edit history">
            <Box display="flex">
              <HistoryIcon
                sx={{
                  ...defaultStyleIcon,
                }}
                cursor="pointer"
                width={15}
                height={17}
                onClick={() => {
                  setSelectedLink((row as any)[0]);
                  setOpenHistoryModal(true);
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'assetTitle',
      headerName: 'Assets',
      width: 250,
      renderCell: ({ value }) => (
        <Tooltip title={value}>
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: '100%',
            }}
          >
            {value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'url',
      headerName: 'Link',
      width: 350,
      renderCell: ({ value }) => (
        <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
          <Tooltip title={value}>
            <Link
              className="ellipsis"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                width: '90%',
              }}
              href={value}
              target="_blank"
            >
              {value}
            </Link>
          </Tooltip>
          <LinkPreview src={value} isImage={false} />
        </Box>
      ),
      filterable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) => <LinkReviewStatus status={value} />,
      sortable: false,
      filterable: false,
    },
    {
      field: 'assetCategory',
      headerName: 'Category',
      width: 180,
      renderCell: ({ value }) => {
        return LinkReviewCategoryMapper[value as keyof typeof LinkReviewCategoryMapper];
      },
      sortable: false,
      filterable: false,
    },
    {
      field: 'episodes',
      headerName: 'Episodes',
      width: 80,
      renderCell: ({ value }) => (
        <Box>
          <span>
            <Typography fontSize={10}>Episodes</Typography>
            {value || 'N/A'}
          </span>
        </Box>
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: 'note',
      headerName: 'Notes',
      width: 150,
      renderCell: ({ row, value }) => (
        <Tooltip
          title={value}
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: '400px',
                wordBreak: 'break-word',
                whiteSpace: 'pre-line',
              },
            },
          }}
        >
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: '95%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '4px',
                width: 'fit-content',
              }}
            >
              <Typography fontSize={10}>Notes</Typography>
              <EditIcon
                sx={{
                  fontSize: '14px',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#b4dffd',
                  },
                }}
                onClick={() => {
                  setSelectedLink((row as any)[0]);
                  setEditingNote(value);
                  setOpenNoteModal(true);
                }}
              />
            </Box>
            {value || '--'}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Update date',
      width: 120,
      renderCell: ({ value }) => (
        <Box>
          <span>
            <Typography fontSize={10}>Update date</Typography>
            {value ? dayjs(value).format(DATE_FORMAT) : 'N/A'}
          </span>
        </Box>
      ),
      filterable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Date of addition',
      width: 120,
      renderCell: ({ value }) => (
        <Box>
          <span>
            <Typography fontSize={10}>Date of addition</Typography>
            {value ? dayjs(value).format(DATE_FORMAT) : 'N/A'}
          </span>
        </Box>
      ),
      filterable: false,
    },
    {
      field: 'bps',
      headerName: 'Email Address',
      width: 200,
      renderCell: ({ value }) => (
        <Tooltip title={value}>
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: '100%',
            }}
          >
            <Typography fontSize={10}>Email Address</Typography>
            {value || 'N/A'}
          </Box>
        </Tooltip>
      ),
      sortable: false,
      filterable: false,
    },
  ];
};
